// src/components/forms/order/OrderBodegaEntregaSection.tsx
// Sección: elección de bodega + fecha de entrega (opcional)

import React from 'react'
import type {
  BodegaRecogidaSeleccion,
  RecogeRespuesta
} from '../../../hooks/order/useRecogeEnBodega'
import { isBodegaSinRecoge } from '../../../hooks/order/useRecogeEnBodega'
import { bodegaFacturacion } from '../../../assets/data/arrays'
import { Input, Select, Switch } from '../../ui'
import { FormField } from '../../ui/Form'
import SectionTitle from '../../ui/SectionTitle'
import { Square3Stack3DIcon, CalendarDaysIcon } from '@heroicons/react/24/outline'

interface OrderBodegaEntregaSectionProps {
  mode: 'view' | 'edit' | 'create'
  fieldsLocked?: boolean
  bodegaRecogidaSeleccion: BodegaRecogidaSeleccion
  onBodegaRecogidaSeleccionChange: (value: BodegaRecogidaSeleccion) => void
  bodegaRecogidaError?: string
  recogeRespuesta: RecogeRespuesta
  onRecogeRespuestaChange: (value: RecogeRespuesta) => void
  recogeError?: string
  fechaEntregaActiva: boolean
  onFechaEntregaActivaChange: (value: boolean) => void
  fechaEntrega: string
  onFechaEntregaChange: (value: string) => void
  fechaEntregaError?: string
}

const BODEGA_OPTIONS = [
  { value: '', label: 'Seleccione' },
  ...bodegaFacturacion
]

const RECOGE_OPTIONS = [
  { value: '', label: 'Seleccione' },
  { value: 'si', label: 'Sí' },
  { value: 'no', label: 'No' }
]

export function OrderBodegaEntregaSection({
  mode,
  fieldsLocked = false,
  bodegaRecogidaSeleccion,
  onBodegaRecogidaSeleccionChange,
  bodegaRecogidaError,
  recogeRespuesta,
  onRecogeRespuestaChange,
  recogeError,
  fechaEntregaActiva,
  onFechaEntregaActivaChange,
  fechaEntrega,
  onFechaEntregaChange,
  fechaEntregaError
}: OrderBodegaEntregaSectionProps) {
  const locked = mode === 'view' || fieldsLocked
  const sinRecoge = isBodegaSinRecoge(bodegaRecogidaSeleccion)
  const recogeDisabled = locked || !bodegaRecogidaSeleccion || sinRecoge
  const fechaDisabled = locked || !fechaEntregaActiva

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <SectionTitle icon={Square3Stack3DIcon}>Bodega de Facturación</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label="Seleccione la bodega"
            required
            error={bodegaRecogidaError}
          >
            <Select
              value={bodegaRecogidaSeleccion}
              onChange={(e) =>
                onBodegaRecogidaSeleccionChange(e.target.value as BodegaRecogidaSeleccion)
              }
              disabled={locked}
              readOnly={locked}
              mode={mode}
              options={BODEGA_OPTIONS}
              placeholder="Seleccione"
              error={!!bodegaRecogidaError}
              errorMessage={bodegaRecogidaError}
              aria-label="Seleccione la bodega"
            />
          </FormField>

          <FormField
            label="Cliente recoge en bodega"
            required
            error={recogeError}
          >
            <Select
              value={recogeRespuesta}
              onChange={(e) => onRecogeRespuestaChange(e.target.value as RecogeRespuesta)}
              disabled={recogeDisabled}
              readOnly={recogeDisabled}
              mode={mode}
              options={RECOGE_OPTIONS}
              placeholder="Seleccione"
              error={!!recogeError}
              errorMessage={recogeError}
              aria-label="Cliente recoge en bodega"
            />
          </FormField>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <SectionTitle icon={CalendarDaysIcon} noMargin>
            Fecha de entrega
          </SectionTitle>
          <Switch
            checked={fechaEntregaActiva}
            onChange={onFechaEntregaActivaChange}
            disabled={locked}
            label="¿enviar este pedido en una fecha específica?"
            size="sm"
            className="shrink-0 gap-1.5"
          />
        </div>
        <FormField label="Fecha de entrega" error={fechaEntregaError}>
          <Input
            type="date"
            value={fechaEntrega}
            onChange={(e) => onFechaEntregaChange(e.target.value)}
            readOnly={fechaDisabled}
            disabled={fechaDisabled}
            mode={mode}
            error={!!fechaEntregaError}
            errorMessage={fechaEntregaError}
            aria-label="Fecha de entrega"
          />
        </FormField>
      </div>
    </div>
  )
}
